import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const ServiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 60,
    price: 0,
    active: true,
    category: ''
  });
  
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingService, setLoadingService] = useState(isEditing);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState('');
  
  const { name, description, duration, price, active, category } = formData;
  
  // Fetch service categories based on business category
  useEffect(() => {
    const fetchServiceCategories = async () => {
      if (!user || user.role !== 'business') return;
      
      setLoadingCategories(true);
      
      try {
        // Get predefined service categories based on business category
        const businessCategory = user.businessDetails?.category || '';
        
        // These are just examples - you can customize these for each business category
        const categoriesByBusinessType = {
          'Barber Shop': ['Haircut', 'Beard Trim', 'Hair Treatment', 'Shaving', 'Other'],
          'Hospital': ['Consultation', 'Check-up', 'Procedure', 'Test', 'Follow-up', 'Other'],
          'Auto Service Center': ['Maintenance', 'Repair', 'Inspection', 'Detailing', 'Other'],
          'Dental Care Center': ['Check-up', 'Cleaning', 'Treatment', 'Surgery', 'Cosmetic', 'Other'],
          'Fitness Studio': ['Personal Training', 'Group Class', 'Assessment', 'Consultation', 'Other'],
          'Hair & Style Salon': ['Haircut', 'Styling', 'Coloring', 'Treatment', 'Extensions', 'Other'],
          'Legal Consultancy': ['Consultation', 'Document Review', 'Filing', 'Representation', 'Other'],
          'Spa & Massage Center': ['Massage', 'Facial', 'Body Treatment', 'Nail Care', 'Other'],
          'Tech Repair Shop': ['Repair', 'Diagnostic', 'Upgrade', 'Data Recovery', 'Setup', 'Other'],
          'Restaurant': ['Reservation', 'Private Event', 'Catering', 'Tasting', 'Other'],
          'Other': ['Consultation', 'Service', 'Assessment', 'Follow-up', 'Other']
        };
        
        // Get categories for this business type or default to "Other" categories
        const categories = categoriesByBusinessType[businessCategory] || categoriesByBusinessType['Other'];
        setServiceCategories(categories);
        
        // Set default category if not editing
        if (!isEditing && categories.length > 0) {
          setFormData(prev => ({ ...prev, category: categories[0] }));
        }
      } catch (error) {
        console.error('Error fetching service categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    fetchServiceCategories();
  }, [user, isEditing]);
  
  // Fetch service data if editing
  useEffect(() => {
    const fetchService = async () => {
      if (!isEditing) return;
      
      try {
        const res = await api.get(`/api/services/${id}`);
        setFormData({
          name: res.data.name,
          description: res.data.description,
          duration: res.data.duration,
          price: res.data.price,
          active: res.data.active,
          category: res.data.category || ''
        });
      } catch (error) {
        console.error('Error fetching service:', error);
        setError('Failed to load service data');
        toast.error('Failed to load service data');
      } finally {
        setLoadingService(false);
      }
    };

    fetchService();
  }, [id, isEditing]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    setError('');
  };
  
  const validateForm = () => {
    if (!name || !description || duration <= 0 || price < 0 || !category) {
      setError('Please fill in all required fields with valid values');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const serviceData = {
        name,
        description,
        duration: Number(duration),
        price: Number(price),
        active,
        category
      };
      
      if (isEditing) {
        await api.put(`/api/services/${id}`, serviceData);
        toast.success('Service updated successfully');
      } else {
        await api.post('/api/services', serviceData);
        toast.success('Service created successfully');
      }
      
      navigate('/business/dashboard');
    } catch (error) {
      console.error('Error saving service:', error);
      setError('Failed to save service');
    } finally {
      setLoading(false);
    }
  };
  
  if (loadingService) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEditing ? 'Edit Service' : 'Add New Service'}
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Service Name"
                value={name}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="category-select-label">Service Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  id="category"
                  name="category"
                  value={category}
                  label="Service Category"
                  onChange={handleChange}
                  disabled={loadingCategories}
                >
                  {serviceCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                multiline
                rows={4}
                value={description}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="duration"
                name="duration"
                label="Duration"
                type="number"
                value={duration}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="price"
                name="price"
                label="Price"
                type="number"
                value={price}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
              />
            </Grid>
            
            {isEditing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={handleChange}
                      name="active"
                      color="primary"
                    />
                  }
                  label="Active (visible to customers)"
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="button"
                  variant="outlined"
                  sx={{ mr: 1 }}
                  onClick={() => navigate('/business/dashboard')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : isEditing ? 'Update Service' : 'Add Service'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default ServiceForm; 